<template>
  <div class="role-and-right-assignment-container">
    <b-dropdown
      v-if="unassignedRoles.length > 0"
      ref="dropdownAssignRoles"
      class="role-assignment-dropdown"
      toggle-class="role-assignment-button"
      menu-class="role-assignment-menu"
      variant="primary"
      no-flip
      right
      :popper-opts="{ positionFixed: true, modifiers: { preventOverflow: { enabled: false } } }"
      :text="$getText('USERS_BUTTON_ADD_USER_ROLES')"
    >
      <b-form-input v-model="searchQuery" type="text" size="sm" :placeholder="$getText('SEARCH_LABEL')" />
      <b-form-checkbox-group v-model="selectedRoleIdsToAssign" stacked class="roles-list my-2">
        <template v-for="(userRole, i) in unassignedRoles">
          <b-form-checkbox
            v-show="getCanShowRole(userRole)"
            :key="i"
            :value="userRole.id"
            @change="onUserRoleChanged(userRole)"
          >
            {{ userRole.name }}
          </b-form-checkbox>
          <template v-for="(subRole, j) in getUnassignedSubRoles(userRole.subRoles)">
            <b-form-checkbox
              v-show="getCanShowRole(subRole)"
              :key="i + '-' + j"
              class="subRole"
              :value="subRole.id"
              @change="onSubRoleChanged(userRole)"
            >
              {{ subRole.name }}
            </b-form-checkbox>
          </template>
        </template>
      </b-form-checkbox-group>
      <div class="d-flex justify-content-center">
        <AulaButton variant="primary" :disabled="isLoading" class="action-btn" @click="onAssignRoles">
          <AulaSpinner v-if="isLoading" class="spinner" />
          {{ $getText('BUTTON_SAVE') }}
        </AulaButton>
      </div>
    </b-dropdown>
    <b-dropdown
      v-if="assignedRoles.length > 0"
      ref="dropdownRemoveRoles"
      toggle-class="role-assignment-button"
      menu-class="role-assignment-menu"
      variant="primary"
      no-flip
      right
      :popper-opts="{ positionFixed: true, modifiers: { preventOverflow: { enabled: false } } }"
      :text="$getText('USERS_BUTTON_REMOVE_USER_ROLES')"
    >
      <b-form-input v-model="searchQuery" type="text" :placeholder="$getText('SEARCH_LABEL')" />
      <b-form-checkbox-group v-model="selectedRoleIdsToRemove" stacked class="roles-list my-2">
        <template v-for="(groupRole, i) in assignedRoles">
          <b-form-checkbox v-show="getCanShowRole(groupRole)" :key="i" :value="groupRole.id">
            {{ groupRole.name }}
          </b-form-checkbox>
        </template>
      </b-form-checkbox-group>
      <div class="d-flex justify-content-center">
        <AulaButton class="action-btn" variant="primary" :disabled="isLoading" @click="onRemoveRoles">
          <AulaSpinner v-if="isLoading" class="spinner" />
          {{ $getText('BUTTON_SAVE') }}
        </AulaButton>
      </div>
    </b-dropdown>
  </div>
</template>
<script>
import { providerKeyEnum } from '../enums/providerKeyEnum';
import AulaButton from './AulaButton.vue';
import { roleEnum } from '../enums/roleEnum';

export default {
  name: 'RoleAndRightAssignments',
  components: { AulaButton },
  inject: {
    getInstitutionProfile: { from: providerKeyEnum.institutionProfile, default: () => () => null },
    getSelectedInstitution: providerKeyEnum.selectedInstitution,
    getRoleDefinitions: providerKeyEnum.roleDefinitions,
    getSelectedUserAssignedRoles: providerKeyEnum.selectedUserAssignedRoles,
    assignRolesToUser: providerKeyEnum.assignRolesToUser,
    getSelectedUsers: providerKeyEnum.getSelectedUsers,
  },
  data() {
    return {
      isLoading: false,
      searchQuery: '',
      selectedRoleIdsToAssign: [],
      selectedRoleIdsToRemove: [],
    };
  },
  computed: {
    institutionProfile() {
      return this.getInstitutionProfile();
    },
    selectedInstitution() {
      return this.getSelectedInstitution();
    },
    selectedUsers() {
      if (this.institutionProfile) {
        return [this.institutionProfile];
      }
      return this.getSelectedUsers();
    },
    canAssignEsdhRole() {
      // ESDH access: temporary institution whitelist.
      // Why: Needed until proper role/permission source is implemented.
      // Tracked in ADO #576890 — https://source.netcompany.com/tfs/NC02/KMTAULA/_workitems/edit/576890
      const whitelist = [
        /** Netcompany */
        '00000', '00003', '00006', '00012', '111111', 'A00943', 'A00969', 'R00154', 'R00257',
        /** Albertslund */
        '165000',
        /** Ballerup */
        '151000', '280565', '281859', 'G16155',
        /** Faxe */
        '320000', '280366',
        /** Frederiksberg */
        '147000', '147052', '147008', 'G17174', 'G17200',
        /** Frederikssund */
        '250000', '280633', '280634', 'G22070',
        /** Faaborg-Midtfyn */
        '430000', '497002', '473001',
        /** Gladsaxe */
        '159000', 'G22788', 'G21573', '159012', '159009',
        /** Gribskov */
        '270000', 'G22437', '213001', '280270',
        /** Halsnæs */
        '260000',
        /** Hedensted */
        '766000', '766001', 'G20859',
        /** Herlev */
        '163000',
        /** Herning */
        '657000', '677003', '657022', '657013',
        /** Hillerød */
        '219000', '280165', 'G18628',
        /** Hjørring */
        '860000', '839004', 'G24904',
        /** Holstebro */
        '661000', '281701', '661012',
        /** Horsens */
        '615000', '615011', 'G22372', 'G22656',
        /** Ikast-Brande */
        '756000', '663006', 'G20969',
        /** Ishøj */
        '183000', '183001', '183002', '183003', '183004', '183005', '183010', '183200', '183212', 'G11769', 'G11771',
        'G11773', 'G11775', 'G11777', 'G11779', 'G11781', 'G11783', 'G11785', 'G11787', 'G22312',
        /** Jammerbugt */
        '849000', '849002', 'G22308',
        /** Lolland */
        '360000', '367001', '280048',
        /** Lyngby-Taarbæk */
        '173000', '173008',
        /** Læsø */
        '825000',
        /** Mariagerfjord */
        '846000', '815001', 'G15615', '719001',
        /** Odense */
        '461000', 'G13468',
        /** Odsherred */
        '306000', '305008', 'G21252',
        /** Randers */
        '730000', '281187', '717002', '717004', '719002', '723001', '723004', '729001', '729002', '729003', '729005',
        '731002', '731003', '731004', '731006', '731008', '731010', '731011', '731013', '731014', '731026', '731030',
        '731200', '731201', '731211', '731901', '731903', '747002', 'G19806', 'G19808', 'G19809', 'G19810', 'G19811',
        'G19812', 'G19813', 'G19814', 'G19815', 'G19816', 'G19817', 'G19818', 'G19819', 'G19820', 'G19821', 'G19822',
        'G20089', 'G20090', 'G20091', 'G20092', 'G20093', 'G20094', 'G20095', 'G20096', 'G20097', 'G20098', 'G20099',
        'G20100', 'G20101', 'G20102', 'G20103', 'G20104', 'G20307', 'G20308', 'G20311', 'G20312', 'G20313', 'G20314',
        'G20315', 'G20316', 'G20317', 'G20318', 'G20319', 'G20405', 'G20406', 'G20407', 'G20408', 'G20409', 'G20452',
        'G20453', 'G20500', 'G20501', 'G20502', 'G20503', 'G20504', 'G20505', 'G20506', 'G20507', 'G20508', 'G20509',
        'G20521', 'G20522', 'G20523', 'G20524', 'G20525', 'G20526', 'G20527', 'G20528', 'G20537', 'G20538', 'G20539',
        'G20540', 'G20541', 'G20542', 'G20543', 'G20549', 'G20550', 'G23655', 'G24996', 'G25183', 'G25628', 'G25965',
        'G27151', 'G27152',
        /** Rebild */
        '840000', '845003', '833004', '280376', 'G17744', 'G18311',
        /** Rudersdal */
        '230000', '281408', 'G16912',
        /** Rødovre */
        '175000', '175004', '175003', 'G23672', 'G23666',
        /** Skanderborg */
        '746000', 'G24159', 'G26499', 'G21939', '715002', '737004',
        /** Solrød */
        '269000', '269001', '269005', 'G22075', 'G23541',
        /** Sorø */
        '340000',
        /** Stevns */
        '336000', '389008', 'G18368',
        /** Thisted */
        '787000', '787024', 'G22751',
        /** Tønder */
        '550000', 'G19647', '531005',
        /** Tårnby */
        '185000', '185005', '185002', 'G21558',
        /** Vejle */
        '630000', '631012', '605002',
        /** Vesthimmerlands */
        '820000', '827002', 'G20921'
      ];
      return (
        this.selectedUsers.length > 0 && this.selectedUsers.every(user => whitelist.includes(user.institutionCode))
      );
    },
    roleDefinitions() {
      return this.getRoleDefinitions();
    },
    assignedRoles() {
      return this.getSelectedUserAssignedRoles();
    },
    unassignedRoles() {
      const assignedRoleIds = this.assignedRoles.map(role => role.id);
      return this.roleDefinitions.filter(definition => {
        if (assignedRoleIds.includes(definition.id)) {
          return false;
        }
        return this.canAssignEsdhRole || [roleEnum.JOURNALING_TO_ESDH].includes(parseInt(definition.id)) === false;
      });
    },
  },
  methods: {
    getCanShowRole(role) {
      return !this.searchQuery || role.name.toLowerCase().includes(this.searchQuery.toLowerCase());
    },
    getUnassignedSubRoles(subRoles) {
      if (!subRoles) {
        return [];
      }
      const assignedRoleIds = this.assignedRoles.map(role => role.id);
      return subRoles.filter(subRole => {
        if (assignedRoleIds.includes(subRole.id)) {
          return false;
        }
        return (
          this.canAssignEsdhRole ||
          [roleEnum.HANDLE_FILE_SENT_TO_ESDH, roleEnum.HANDLE_MUNICIPAL_FILE_SENT_TO_ESDH].includes(
            parseInt(subRole.id)
          ) === false
        );
      });
    },
    onUserRoleChanged(userRole) {
      if (!userRole.subRoles) {
        return;
      }
      if (this.selectedRoleIdsToAssign.includes(userRole.id)) {
        userRole.subRoles.forEach(subRole => {
          if (!this.selectedRoleIdsToAssign.includes(subRole.id)) {
            this.selectedRoleIdsToAssign.push(subRole.id);
          }
        });
      } else {
        const subRoleIds = userRole.subRoles.map(subRole => subRole.id);
        this.selectedRoleIdsToAssign = this.selectedRoleIdsToAssign.filter(id => subRoleIds.includes(id) === false);
      }
    },
    onSubRoleChanged(userRole) {
      const assignableSubRoles = this.getUnassignedSubRoles(userRole.subRoles);
      if (assignableSubRoles.every(subRole => this.selectedRoleIdsToAssign.includes(subRole.id))) {
        this.selectedRoleIdsToAssign.push(userRole.id);
      } else {
        this.selectedRoleIdsToAssign = this.selectedRoleIdsToAssign.filter(roleId => userRole.id !== roleId);
      }
    },
    async onAssignRoles() {
      await this.setRoleAssignments();
      this.$refs.dropdownAssignRoles.visible = false;
    },
    async onRemoveRoles() {
      await this.setRoleAssignments();
      this.$refs.dropdownRemoveRoles.visible = false;
    },
    async setRoleAssignments() {
      this.isLoading = true;
      await this.assignRolesToUser({
        roleIdsToAssign: this.selectedRoleIdsToAssign,
        roleIdsToUnAssign: this.selectedRoleIdsToRemove,
      });
      this.isLoading = false;
      this.resetSelectedValues();
    },
    resetSelectedValues() {
      this.selectedRoleIdsToAssign = [];
      this.selectedRoleIdsToRemove = [];
    },
  },
};
</script>
<style scoped lang="scss">
@use '../assets/scss/core/variables' as *;

.role-and-right-assignment-container {
  display: flex;
  gap: 1rem;

  ::v-deep .role-assignment-button.btn {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 1rem 2.5rem;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
    background-color: var(--color-primary-darker);

    &:hover {
      background-color: var(--color-primary-dark);
    }

    &[aria-expanded='true'] {
      z-index: $aula-modal-button-z-index;
      background-color: transparent;
      color: var(--color-primary-dark);

      &::after {
        content: '\e906';
        font-size: 0.75rem;
        margin-left: 8px;
      }
    }
  }
}

input.form-control {
  --input-border-color: var(--color-grey-base);
}

.spinner {
  width: 50px;
  height: 50px;
  position: absolute;
  transform: translate(-25%, -25%);
}
</style>
